import PropTypes from "prop-types";
import React from "react";
import { Trans } from "react-i18next";
import '../../../assets/styles/quittanceIndemnite.css';
import { libellePrejudice } from "../../../utils/typePrejudice";
import axiosInstance from "../../../variables/api/axiosInstance";
import logoLocal from "../../../assets/images/logo.png";
import BlocSignature from '../../../components/BlocSignature';
import EnteteDocumentBncb from './EnteteDocumentBncb';
import PiedDocumentBncb from './PiedDocumentBncb';
import { montantEnLettresAnglais, tDocument } from '../../../i18n/documents';

const DASH = "-";

const resolveBrandingUrl = (path) => {
    if (!path) return null;
    if (/^(https?:|data:)/i.test(path)) return path;
    const base = (axiosInstance.defaults.baseURL || "").replace(/\/$/, "");
    return `${base}${path}`;
};

const safeMontant = (value) => Number(value) || 0;

const displayNumbers = (value) =>
    String(value).replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");

const frenchDateOnly = (value) => {
    if (!value) return DASH;
    const d = new Date(value);
    if (isNaN(d.getTime())) return DASH;
    return d.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

const numberToWordsFrench = (value) => {
    const n = Math.trunc(safeMontant(value));
    if (n === 0) return "Zéro franc CFA";

    const unites = ["", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf",
        "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize",
        "dix-sept", "dix-huit", "dix-neuf"];
    const dizaines = ["", "", "vingt", "trente", "quarante", "cinquante", "soixante",
        "soixante", "quatre-vingt", "quatre-vingt"];

    const centaine = (nb) => {
        if (nb < 20) return unites[nb];
        const d = Math.floor(nb / 10);
        const u = nb % 10;
        if (d === 7 || d === 9) return dizaines[d] + (u === 1 && d === 7 ? "-et-" : "-") + unites[10 + u];
        return dizaines[d] + (u === 1 && d < 8 ? "-et-" : u > 0 ? "-" : "") + unites[u];
    };

    const groupe = (nb) => {
        if (nb === 0) return "";
        if (nb < 100) return centaine(nb);
        const c = Math.floor(nb / 100);
        const r = nb % 100;
        const pref = c === 1 ? "cent" : unites[c] + " cent";
        return r === 0 ? pref + (c > 1 ? "s" : "") : pref + " " + centaine(r);
    };

    let result = "";
    const milliards = Math.floor(n / 1_000_000_000);
    const millions = Math.floor((n % 1_000_000_000) / 1_000_000);
    const milliers = Math.floor((n % 1_000_000) / 1_000);
    const reste = n % 1_000;

    if (milliards) result += groupe(milliards) + " milliard" + (milliards > 1 ? "s" : "") + " ";
    if (millions) result += groupe(millions) + " million" + (millions > 1 ? "s" : "") + " ";
    if (milliers) result += (milliers === 1 ? "mille" : groupe(milliers) + " mille") + " ";
    if (reste) result += groupe(reste);

    return (
        result.trim().charAt(0).toUpperCase() +
        result.trim().slice(1) +
        " franc" +
        (n > 1 ? "s" : "") +
        " CFA"
    );
};

const asLabel = (value) => {
    if (value == null) return null;
    if (typeof value === "string") return value.trim() || null;
    if (typeof value === "object") {
        return value.raisonSociale || value.nom || value.libelle || value.label || null;
    }
    return String(value);
};

/* Traduction anglaise d'un intitule de la quittance bilingue. */
const TRADUCTION = { fontWeight: 400, fontSize: "0.82em" };

const findPrimaryAdversaire = (sinistre) =>
    sinistre?.adversaires?.[0] ?? sinistre?.adversaire ?? null;

const QuittanceIndemnite = React.forwardRef((props, ref) => {
    const { sinistre, decaissement } = props;
    /* LE FRANCAIS FAIT FOI. Pour un beneficiaire anglophone (`langue` 'en'), la quittance
       reste celle d'avant, chaque intitule suivi de sa traduction et l'engagement repris
       en anglais sous le texte francais : il comprend ce qu'il signe, et la piece reste
       la meme pour le Bureau et la compagnie. */
    const bilingue = props?.langue === "en";
    const fr = tDocument("fr");
    const en = tDocument("en");
    /* La traduction suit l'intitule, en plus petit et sans graisse : elle se lit sans
       concurrencer le francais, et tient dans les cellules etroites sans les replier. */
    const lib = (cle, valeurs) => (bilingue
        ? <>{fr(cle, valeurs)}<span style={TRADUCTION}> / {en(cle, valeurs)}</span></>
        : fr(cle, valeurs));
    const texte = (cle) => (bilingue ? `${fr(cle)} / ${en(cle)}` : fr(cle));
    /* Chargee AVANT le rendu : ce document est serialise par renderToStaticMarkup,
       ou aucun effet React ne s'execute et ou BlocSignature ne chargerait rien. */
    const signature = props?.signature || null;

    const organisme = props?.organisme || sinistre?.contrat?.organisme;
    /* L'en-tête est celui du BUREAU qui délivre la quittance (Carte Brune), pas celui de
       la compagnie qui paie : `organisme` porte l'assureur principal du dossier, et la
       quittance sortait au nom d'une compagnie étrangère. */
    const bureau = props?.bureau || organisme;
    const primaryAdversaire = findPrimaryAdversaire(sinistre);

    const montant = safeMontant(
        decaissement?.montant ?? decaissement?.montantPaye ?? decaissement?.montantGlobal
    );
    const montantFormate = displayNumbers(Math.trunc(montant));
    const montantEnLettres = numberToWordsFrench(montant);

    const assureName =
        [primaryAdversaire?.personneNom, primaryAdversaire?.personnePrenom]
            .filter(Boolean).join(" ") || DASH;

    // Type connu du referentiel seulement : un code inconnu ou absent ne s'affiche pas.
    const prejudiceType = libellePrejudice(decaissement?.typePrejudice)
        ? texte(`quittance.prejudices.${decaissement.typePrejudice}`)
        : "";
    const prejudicesLibelle =
        [prejudiceType, decaissement?.motifComplement]
            .filter(Boolean).join(" - ") || DASH;

    const organismeRaisonSociale = asLabel(organisme) || "";

    /* LE BUREAU HOMOLOGUE DONT ON A RECU LE CHEQUE. C'est pour son compte que le
       Bureau paie : la decharge porte donc son nom, et non celui de la compagnie
       d'assurance ni du beneficiaire.

       Ces deux variables avaient ete supprimees le 20 septembre, quand « en
       l'acquit de » designait le beneficiaire ; elles reviennent avec la regle. */
    const bureauHomologue = sinistre?.bureauHomologueDestinataire;
    const homologueRaisonSociale =
        asLabel(bureauHomologue) ||
        asLabel(sinistre?.organismeHomologueRaisonSociale) ||
        DASH;
    const assureurAdverseLabel =
        asLabel(primaryAdversaire?.assuranceAdverse) ||
        asLabel(sinistre?.assureurDeclarant) ||
        DASH;
    const banqueLabel =
        asLabel(decaissement?.banque) ||
        asLabel(decaissement?.banqueCheque) ||
        DASH;
    const numeroChequeLabel =
        asLabel(decaissement?.numeroCheque) ||
        asLabel(decaissement?.numeroChequeEmis) ||
        DASH;
    const dateChequeRaw =
        decaissement?.dateEmissionCheque ||
        decaissement?.dateCheque ||
        null;

    // URL backend relative (`/v1/...`) - préfixe baseURL sinon résolu contre l'origine du frontend.
    const logoImage = resolveBrandingUrl(bureau?.logo || bureau?.headerImageUrl);

    const villeImpression = bureau?.ville || organisme?.ville || "Lomé";

    /* Le bénéficiaire est le libellé saisi au règlement : selon le type, le nom de
       la victime, la raison sociale d'un organisme ou le nom d'un expert. C'est lui
       qui donne quittance, et non plus le bureau homologue - la décharge est signée
       par celui qui reçoit l'argent. */
    const beneficiaireLabel = asLabel(decaissement?.beneficiaire) || DASH;

    /* Repli sur la date du chèque : un paiement peut être imprimé avant que
       `datePaiement` ne soit posée, et un en-tête sans date vaut moins qu'une date
       approchante tirée du même règlement. */
    const dateReglementRaw = decaissement?.datePaiement || dateChequeRaw;
    const dateReglementLabel = dateReglementRaw
        ? frenchDateOnly(dateReglementRaw)
        : DASH;

    const dateAccidentRaw = sinistre?.dateAccident || sinistre?.dateSinistre;
    const numeroSinistreLocal =
        sinistre?.numeroSinistreLocal ||
        sinistre?.numeroSinistreManuel ||
        sinistre?.numeroSinistreM ||
        sinistre?.numSinistre ||
        DASH;
    const numeroSinistreHomologueLabel =
        sinistre?.numeroSinistreHomologue || DASH;
    const numeroSinistreAssureurLabel =
        sinistre?.numeroSinistreAssureur || DASH;

    const refRows = [
        { cle: "dateAccident", value: dateAccidentRaw ? frenchDateOnly(dateAccidentRaw) : DASH },
        { cle: "sinistreBncb", value: numeroSinistreLocal },
        { cle: "sinistreHomologue", value: numeroSinistreHomologueLabel },
        { cle: "assureur", value: assureurAdverseLabel },
        { cle: "sinistreAssureur", value: numeroSinistreAssureurLabel },
        { cle: "assure", value: assureName },
    ];

    /* Bilingue : quelques millimetres repris aux marges et aux blancs, pour que les
       traductions ajoutees tiennent sur la meme page A4 (mesure a l'impression). */
    return (
        <div className="quittance-container" ref={ref} style={bilingue ? { paddingTop: "9mm", paddingBottom: "8mm" } : undefined}>

            {/* En-tete repris de l'offre CIMA, via le composant commun. Le filet
                vert qu'il porte remplace l'ancien `quittance-separator`. */}
            <EnteteDocumentBncb
                bureau={bureau}
                logo={logoImage || logoLocal}
                mentionDroite={bilingue
                    ? <>{lib("quittance.dateReglement")} : {dateReglementLabel}</>
                    : `${fr("quittance.dateReglement")} : ${dateReglementLabel}`}
            />

            {/*  Titre  */}
            <div className="quittance-title-wrapper">
                <h2 className="quittance-title">
                    QUITTANCE D&apos;INDEMNITÉ / INDEMNITY SLIP
                </h2>
            </div>

            {/*  Références du sinistre  */}
            <div className="quittance-block">
                <div className="quittance-block-title">{lib("quittance.references.titre")}</div>
                <table className="quittance-ref-table">
                    <tbody>
                        {refRows.map(({ cle, value }) => (
                            <tr key={cle}>
                                <td className="quittance-ref-label">{lib(`quittance.references.${cle}`)}</td>
                                <td className="quittance-ref-value">{value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/*  Références de paiement  */}
            <div className="quittance-block">
                <div className="quittance-block-title">{lib("quittance.paiement.titre")}</div>
                <table className="quittance-payment-table">
                    <thead>
                        <tr>
                            <th>{lib("quittance.paiement.mode")}</th>
                            <th>{lib("quittance.paiement.numeroCheque")}</th>
                            <th>{lib("quittance.paiement.banque")}</th>
                            <th>{lib("quittance.paiement.dateCheque")}</th>
                            {/* Bénéficiaire, et non montant : celui-ci figure déjà deux
                                fois plus bas, dans « TOTAL PAYÉ EN FCFA » et en chiffre
                                dans le texte d'engagement. */}
                            <th>{lib("quittance.paiement.beneficiaire")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{lib("quittance.paiement.cheque")}</td>
                            <td>{numeroChequeLabel}</td>
                            <td>{banqueLabel}</td>
                            <td>{dateChequeRaw ? frenchDateOnly(dateChequeRaw) : DASH}</td>
                            <td style={{ fontWeight: 700 }}>{beneficiaireLabel}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/*  Préjudices réglés et total : deux encadrés distincts, comme la maquette.
                Un seul tableau à deux colonnes liait la largeur du libellé à celle du
                montant ; séparés, le libellé peut s'étendre sans écraser la somme. */}
            <div className="quittance-block quittance-totaux">
                <div className="quittance-encadre">
                    <div className="quittance-encadre-titre">{lib("quittance.prejudicesRegles")}</div>
                    <div className="quittance-encadre-valeur">{prejudicesLibelle}</div>
                </div>
                <div className="quittance-encadre quittance-encadre--total">
                    <div className="quittance-encadre-titre">{lib("quittance.totalPaye")}</div>
                    <div className="quittance-encadre-valeur quittance-encadre-montant">
                        <span className="quittance-montant">{montantFormate}</span>
                    </div>
                </div>
            </div>

            {/*  Texte d'engagement  */}
            {/* Interligne resserre en bilingue : la traduction de l'engagement s'ajoute
                sous le francais, et la quittance doit tenir sur une page. */}
            <div className="quittance-engagement" style={bilingue ? { lineHeight: 1.5, marginTop: 10 } : undefined}>
                {/* QUI RECONNAIT, ET POUR QUI. « Nous soussignés » est le
                    BENEFICIAIRE : c'est lui qui recoit et qui signe en bas de page.
                    « En l'acquit de » est le BUREAU HOMOLOGUE dont le cheque provient :
                    c'est pour son compte que le Bureau paie, et la decharge le liberent
                    lui. Ni l'un ni l'autre n'est la compagnie d'assurance. */}
                <p><Trans t={fr} i18nKey="quittance.engagement.soussignes" values={{ beneficiaire: beneficiaireLabel }} components={{ b: <b /> }} /></p>
                <p>
                    <Trans t={fr} i18nKey="quittance.engagement.recu" values={{ organisme: organismeRaisonSociale }} components={{ b: <b /> }} />
                </p>
                <p>
                    <Trans t={fr} i18nKey="quittance.engagement.acquit" values={{ homologue: homologueRaisonSociale }} components={{ b: <b /> }} />
                </p>
                <p><Trans t={fr} i18nKey="quittance.engagement.enChiffre" values={{ montant: montantFormate }} components={{ m: <b className="quittance-montant" /> }} /></p>
                <p><Trans t={fr} i18nKey="quittance.engagement.enLettre" values={{ montant: montantEnLettres }} components={{ m: <b className="quittance-montant" /> }} /></p>
                <p>{fr("quittance.engagement.reparation")}</p>
                {/* La traduction : les memes six lignes, en un paragraphe sous le francais. */}
                {bilingue && (
                    <p style={{ marginTop: 6, fontSize: 10.5, lineHeight: 1.4, fontStyle: "italic", color: "#374151" }}>
                        <Trans t={en} i18nKey="quittance.engagement.soussignes" values={{ beneficiaire: beneficiaireLabel }} components={{ b: <b /> }} />{" "}
                        <Trans t={en} i18nKey="quittance.engagement.recu" values={{ organisme: organismeRaisonSociale }} components={{ b: <b /> }} />{" "}
                        <Trans t={en} i18nKey="quittance.engagement.acquit" values={{ homologue: homologueRaisonSociale }} components={{ b: <b /> }} />{" "}
                        <Trans t={en} i18nKey="quittance.engagement.enChiffre" values={{ montant: new Intl.NumberFormat("en-GB").format(Math.trunc(montant)) }} components={{ m: <b className="quittance-montant" /> }} />{" "}
                        <Trans t={en} i18nKey="quittance.engagement.enLettre" values={{ montant: montantEnLettresAnglais(montant) }} components={{ m: <b className="quittance-montant" /> }} />{" "}
                        {en("quittance.engagement.reparation")}
                    </p>
                )}
            </div>

            {/*  Signatures  */}
            <div className="quittance-signatures" style={bilingue ? { marginTop: 18 } : undefined}>
                {[lib("quittance.signatures.secretaireExecutif"), lib("quittance.signatures.beneficiaire")].map((label, index) => (
                    <div key={index} className="quittance-signature-col">
                        <span className="quittance-signature-label">{label}</span>
                        {/* La colonne du bénéficiaire reste vide : il signe de sa main,
                            au moment où il reçoit. */}
                        <div className="quittance-signature-space">
                            {index === 0 && (
                                <BlocSignature
                                    codeDocument="QUITTANCE_INDEMNITE"
                                    sinistreTrackingId={sinistre?.sinistreTrackingId}
                                    hauteurMm={14}
                                    alignement="left"
                                    signature={signature}
                                />
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/*  Pied de page  */}
            <div className="quittance-footer">
                <span>
                    {lib("quittance.faitA", {
                        ville: villeImpression,
                        date: decaissement?.dateImpressionQuittance
                            ? frenchDateOnly(decaissement.dateImpressionQuittance)
                            : frenchDateOnly(new Date().toISOString()),
                    })}
                </span>
                <span className="quittance-footer-dots">
                    {bilingue
                        ? "........................................"
                        : "................................................................"}
                </span>
            </div>

            {/* Coordonnees du Bureau, comme au bas de l'offre CIMA. */}
            <PiedDocumentBncb bureau={bureau} />
        </div>
    );
});

QuittanceIndemnite.propTypes = {
    /** 'en' : beneficiaire anglophone, quittance bilingue (le francais fait foi). */
    langue: PropTypes.oneOf(["fr", "en"]),
    decaissement: PropTypes.object,
    sinistre: PropTypes.object,
    organisme: PropTypes.object,
    bureau: PropTypes.object,
};

QuittanceIndemnite.displayName = "QuittanceIndemnite";

export default QuittanceIndemnite;